/** Byte / string helpers and the canonical serialiser used for signing. */

export type Hex = string;

const HEXC = '0123456789abcdef';

export function toHex(b: Uint8Array): Hex {
  let s = '';
  for (let i = 0; i < b.length; i++) s += HEXC[b[i] >> 4] + HEXC[b[i] & 15];
  return s;
}

export function fromHex(h: Hex): Uint8Array {
  if (typeof h !== 'string' || h.length % 2 !== 0) throw new Error('bad hex');
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) {
    const v = parseInt(h.substr(i * 2, 2), 16);
    if (Number.isNaN(v)) throw new Error('bad hex');
    out[i] = v;
  }
  return out;
}

export function concat(...parts: Uint8Array[]): Uint8Array {
  let n = 0;
  for (const p of parts) n += p.length;
  const out = new Uint8Array(n);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

export function fromUtf8(b: Uint8Array): string {
  return new TextDecoder().decode(b);
}

export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

export function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

/* ---------------------------- base32 --------------------------------- *
 * Crockford alphabet: no I, L, O or U, so a recovery key read over the
 * phone or copied off a screen cannot be garbled into a different key.
 * -------------------------------------------------------------------- */

const B32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const B32_INV = (() => {
  const m: Record<string, number> = {};
  for (let i = 0; i < B32.length; i++) m[B32[i]] = i;
  // Forgiving aliases for the characters Crockford excludes.
  m['I'] = 1; m['L'] = 1; m['O'] = 0; m['U'] = 27;
  return m;
})();

export function b32encode(bytes: Uint8Array): string {
  let out = '';
  let bits = 0;
  let acc = 0;
  for (const byte of bytes) {
    acc = (acc << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += B32[(acc >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += B32[(acc << (5 - bits)) & 31];
  return out;
}

export function b32decode(s: string): Uint8Array {
  const clean = s.toUpperCase().replace(/[^0-9A-Z]/g, '');
  const out: number[] = [];
  let bits = 0;
  let acc = 0;
  for (const ch of clean) {
    const v = B32_INV[ch];
    if (v === undefined) throw new Error('bad base32 character: ' + ch);
    acc = (acc << 5) | v;
    bits += 5;
    if (bits >= 8) {
      out.push((acc >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }
  return new Uint8Array(out);
}

/* ------------------------ canonical encoding ------------------------- *
 * Signatures must be over bytes that every implementation reproduces
 * identically. JSON.stringify is not that: key order and number
 * formatting are not pinned. This is: keys sorted, no whitespace,
 * bigints as decimal strings, undefined dropped, floats rejected.
 * -------------------------------------------------------------------- */

export function canonicalize(value: unknown): string {
  const t = typeof value;
  if (value === null) return 'null';
  if (t === 'string') return JSON.stringify(value);
  if (t === 'boolean') return value ? 'true' : 'false';
  if (t === 'bigint') return JSON.stringify((value as bigint).toString(10));
  if (t === 'number') {
    if (!Number.isFinite(value as number)) throw new Error('non-finite number');
    if (!Number.isInteger(value as number)) throw new Error('non-integer number in canonical form');
    return String(value);
  }
  if (Array.isArray(value)) return '[' + value.map(canonicalize).join(',') + ']';
  if (t === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj).filter((k) => obj[k] !== undefined).sort();
    return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalize(obj[k])).join(',') + '}';
  }
  throw new Error('cannot canonicalize ' + t);
}

export function canonicalBytes(value: unknown): Uint8Array {
  return utf8(canonicalize(value));
}

/** Reviver-friendly JSON for wire transport (bigints already strings). */
export function jsonStringify(value: unknown): string {
  return canonicalize(value);
}

/**
 * Copy into a plain ArrayBuffer. WebCrypto's typings reject views over
 * ArrayBufferLike (they could be shared memory), and the copy is cheap
 * next to the key derivation it feeds.
 */
export function toArrayBuffer(u: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(u.length);
  new Uint8Array(out).set(u);
  return out;
}
